/**
 * ChapterDrawer — the table of contents, built from `GET /api/v1/books/:id/chapters`.
 *
 * design.md §5.8 lists it under "chrome that nothing in the subsystem designs specified and 'feels
 * like a real book' requires". It is also the only navigation in P1 that is not a page turn, which
 * makes it the only way to reach chapter 40 without forty swipes.
 *
 * Two things here are not decoration:
 *
 *  · **Reading order comes from `readOrd`, not from `ord`, and it is sorted with
 *    `compareOrdered` — the only orderer (AGENTS.md).** `ord` is the source file's spine order;
 *    `readOrd` is where the chapter belongs when you read the book, and an EPUB whose front matter
 *    is spined after chapter 1 (Calibre does this) makes them different. Sorting on `ord` there
 *    puts the copyright page in the middle of the novel.
 *
 *  · **Role grouping is how the front-matter problem becomes visible.** §4.8: Karti's one measured
 *    R&D failure was narrating the publisher's front matter first and burning GPU on it. A drawer
 *    that shows "Front matter · The book · End matter" as separate groups means an ingest that got
 *    the boundary wrong is obvious at a glance, before a single GPU-second is spent in P2.
 */

import { useEffect, useRef, type ReactElement } from "react";
import type { Chapter, ChapterRole } from "../../../shared/doc.ts";
import { compareOrdered } from "../../../shared/address.ts";
import { estimateSpeechMs } from "../../../shared/narration/rates.ts";

export interface ChapterDrawerProps {
  open: boolean;
  chapters: readonly Chapter[];
  /** The chapter currently mounted in the track. */
  currentOrd: number | null;
  onSelect(ord: number): void;
  onClose(): void;
}

const GROUPS: readonly { role: ChapterRole; label: string }[] = [
  { role: "front", label: "Front matter" },
  { role: "body", label: "The book" },
  { role: "back", label: "End matter" },
];

export function ChapterDrawer(props: ChapterDrawerProps): ReactElement | null {
  const { open, chapters, currentOrd, onSelect, onClose } = props;
  const panelRef = useRef<HTMLDivElement | null>(null);
  const restoreFocusTo = useRef<Element | null>(null);

  useEffect(() => {
    if (!open) return;
    restoreFocusTo.current = document.activeElement;
    // Focus the panel so the arrow keys stop turning pages the moment the drawer is up — the
    // keyboard handler in usePageTurn ignores events from form controls, not from a dialog, and a
    // reader tabbing through forty chapters while the book flips behind them is a real bug.
    panelRef.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      (restoreFocusTo.current as HTMLElement | null)?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  const ordered = [...chapters].sort((a, b) =>
    compareOrdered({ ord: a.readOrd, id: a.id }, { ord: b.readOrd, id: b.id }),
  );

  return (
    <>
      <div className="ab-drawer-scrim" onClick={onClose} aria-hidden="true" />
      <div
        className="ab-drawer"
        role="dialog"
        aria-label="Chapters"
        aria-modal="true"
        tabIndex={-1}
        ref={panelRef}
      >
        <div className="ab-drawer-head">
          <span className="ab-drawer-title">Chapters</span>
          <button type="button" className="ab-btn" onClick={onClose} aria-label="Close chapters">
            Close
          </button>
        </div>

        <ul className="ab-drawer-list">
          {GROUPS.map(({ role, label }) => {
            const inGroup = ordered.filter((c) => c.role === role);
            if (inGroup.length === 0) return null;
            return (
              <li key={role}>
                <div className="ab-drawer-group">{label}</div>
                <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                  {inGroup.map((c) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        className="ab-chapter-btn"
                        aria-current={c.ord === currentOrd ? "true" : undefined}
                        data-mute={c.speak ? undefined : "1"}
                        onClick={() => {
                          onSelect(c.ord);
                          onClose();
                        }}
                      >
                        <span className="ab-chapter-name">{c.title || untitled(c)}</span>
                        <span className="ab-chapter-mins">{minutesLabel(c.charCount)}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </li>
            );
          })}
        </ul>
      </div>
    </>
  );
}

/** A chapter with no title still needs a handle. Never an empty row. */
function untitled(c: Chapter): string {
  return `Chapter ${c.ord}`;
}

/**
 * How long this chapter takes to read aloud.
 *
 * From `shared/narration/rates.ts` and nowhere else. §2.4: four documents once carried four values
 * for this one physical constant (11.5 / 14 / 15.3 / 16.0 chars per second) and the co-read sync
 * axis used the stale one — a systematic ~30% overestimate of every un-narrated stretch. There is
 * one number now and this file imports it.
 *
 * It is an *estimate* in P1 because no clip has been generated yet, and the label says "~" for
 * exactly that reason. In P2 a narrated chapter has measured durations and this becomes the truth.
 */
function minutesLabel(chars: number): string {
  if (!Number.isFinite(chars) || chars <= 0) return "";
  const min = Math.round(estimateSpeechMs(chars) / 60_000);
  return min < 1 ? "<1 min" : `${min} min`;
}
